// src/components/admin/SuperAdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, Users, Shield, Clock, Radio, MapPin, Edit2, Map,
  UserCheck, Settings, ChevronLeft, ChevronRight, Search,
  CheckCircle, XCircle, AlertTriangle, Wifi, WifiOff,
  RefreshCw, Eye, X as CloseX, Trash2, Ban, RotateCcw,
  GraduationCap, Building, FileText, Download, Lock, Unlock,
  TrendingUp, Activity, Info, Save,
  MoreVertical, Calendar, LogOut, DollarSign, Send, AlertOctagon,
  Key, Copy, Plus, ZapOff, MessageSquare, User, ScrollText, BarChart2,
  Sprout, Droplets, Thermometer, FlaskConical, Zap,
} from 'lucide-react';
import NotificationBell from '../common/NotificationBell';
import MessagingPanel from '../common/MessagingPanel';
import ProfilePage from '../profile/ProfilePage';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from 'recharts';
import {
  fetchAllUsers, fetchUserStats, fetchPendingVerifications,
  approveUser, rejectUser, updateUser, updateUserRole,
  suspendUser, activateUser, deleteUser,
  deleteAllSuspended, adminResendOtp, adminForceVerify,
  assignDevicesToUser, getDocumentUrl,
  fetchAllApiKeys, generateApiKeyForUser, revokeApiKey,
  adminCreateUser,
} from '../../services/adminService';
import { fetchWeatherDevices } from '../../services/weatherService';
import { fetchSoilDevices, updateSoilDevice } from '../../services/soilService';
import { getAllDevicesMeta, saveDeviceMeta, resetDeviceMeta } from '../../services/deviceMetaService';
import { fetchTrafficStats } from '../../services/trafficService';
import { getRoleDisplayName, USER_ROLES } from '../../utils/roles';
import DevicesMap from '../common/DevicesMap';
import WeatherStationDashboard from '../dashboard/WeatherStationDashboard';
import LogsPanel from './LogsPanel';

// ── Constants ────────────────────────────────────────────────────────────────

// Business model tiers for the Anga Platform
const ACCESS_TIERS = [
  {
    value: 'basic',
    label: 'Basic',
    badge: 'Free',
    price: 'KES 0/mo',
    color: 'gray',
    features: ['Up to 3 nearest stations', 'Current readings only', '7-day chart history', 'No data export'],
  },
  {
    value: 'education',
    label: 'Education',
    badge: 'Schools & Students',
    price: 'KES 500/mo',
    color: 'blue',
    features: ['Assigned school station + 5 nearby', '30-day history', 'CSV export for academic use', 'School dashboard'],
  },
  {
    value: 'research',
    label: 'Research',
    badge: 'NGOs & Researchers',
    price: 'KES 2,500/mo',
    color: 'purple',
    features: ['All stations', '1-year history', 'CSV & JSON export', 'API access (rate-limited)'],
  },
  {
    value: 'enterprise',
    label: 'Enterprise',
    badge: 'Organizations',
    price: 'Custom',
    color: 'green',
    features: ['All stations', 'Unlimited history', 'Bulk export', 'Priority support', 'White-label option'],
  },
  {
    value: 'custom',
    label: 'Custom',
    badge: 'Admin-assigned',
    price: 'N/A',
    color: 'orange',
    features: ['Manually selected stations', 'Admin-defined permissions', 'Used for partners & special cases'],
  },
  {
    value: 'custodian',
    label: 'Custodian',
    badge: 'Station Keeper',
    price: 'Free',
    color: 'green',
    features: ['Free access to assigned station(s)', 'Reward for maintaining equipment', 'Real-time + historical data', 'Admin-assigned stations only'],
  },
];

const ROLE_OPTIONS = [
  { value: 'user',        label: 'User (Standard)' },
  { value: 'custodian',   label: 'Custodian (Station Manager)' },
  { value: 'admin',       label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
];

const TIER_OPTIONS = [
  { value: 'basic',        label: 'Basic — Free' },
  { value: 'education',    label: 'Education' },
  { value: 'professional', label: 'Professional' },
  { value: 'enterprise',   label: 'Enterprise' },
];

const PIE_COLORS = ['#16a34a', '#f59e0b', '#dc2626'];

// ── Tiny utilities ───────────────────────────────────────────────────────────

const fmtGw = (id = '') =>
  id.replace(/^(su-wp-|wp-)/, '').replace(/-gateway$/, '').replace(/-wirelessplanet$/, '')
    .split('-').map(w => w[0]?.toUpperCase() + w.slice(1)).join(' ') || id;

const timeAgo = (iso) => {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const gwHealth = (lastSeen) => {
  if (!lastSeen) return { label: 'Unknown', color: 'gray', dot: 'bg-gray-400' };
  const h = (Date.now() - new Date(lastSeen).getTime()) / 3600000;
  if (h < 2)  return { label: 'Online',   color: 'green',  dot: 'bg-green-500'  };
  if (h < 24) return { label: 'Degraded', color: 'yellow', dot: 'bg-yellow-500' };
  return       { label: 'Offline',   color: 'red',    dot: 'bg-red-500'    };
};

const rssiLabel = (rssi) => {
  if (rssi == null || rssi === '—') return { text: '—',        color: 'text-gray-400' };
  const v = parseFloat(rssi);
  if (v > -70)  return { text: 'Excellent', color: 'text-green-600' };
  if (v > -85)  return { text: 'Good',      color: 'text-blue-600'  };
  if (v > -100) return { text: 'Fair',      color: 'text-yellow-600'};
  return               { text: 'Poor',      color: 'text-red-500'   };
};

// ── Small reusable components ────────────────────────────────────────────────

const KpiCard = ({ label, value, icon, sub, color = 'blue' }) => {
  const colors = {
    blue:   'bg-blue-50   text-blue-700   border-blue-100',
    green:  'bg-green-50  text-green-700  border-green-100',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-100',
    red:    'bg-red-50    text-red-700    border-red-100',
    purple: 'bg-purple-50 text-purple-700 border-purple-100',
    gray:   'bg-gray-50   text-gray-700   border-gray-100',
  };
  return (
    <div className={`rounded-xl border p-4 flex items-center gap-4 ${colors[color]}`}>
      <div className="w-10 h-10 rounded-lg bg-white/70 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <p className="text-xs font-medium opacity-70 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold">{value ?? '—'}</p>
        {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
};

const Badge = ({ children, color = 'gray' }) => {
  const cls = {
    green:  'bg-green-100  text-green-800',
    red:    'bg-red-100    text-red-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    blue:   'bg-blue-100   text-blue-800',
    purple: 'bg-purple-100 text-purple-800',
    gray:   'bg-gray-100   text-gray-700',
    orange: 'bg-orange-100 text-orange-800',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cls[color] || cls.gray}`}>
      {children}
    </span>
  );
};

const EmptyState = ({ icon: Icon, title, sub }) => (
  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
    <Icon className="w-12 h-12 mb-3 opacity-30" />
    <p className="font-semibold text-gray-500">{title}</p>
    {sub && <p className="text-sm mt-1">{sub}</p>}
  </div>
);

const SectionHeader = ({ title, sub, actions }) => (
  <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
    <div>
      <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      {sub && <p className="text-sm text-gray-500 mt-0.5">{sub}</p>}
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

// ── Main Dashboard ───────────────────────────────────────────────────────────

// Default tier config — stored in localStorage so changes persist
const DEFAULT_TIER_CONFIG = {
  basic:      { price: 0,    currency: 'KES', period: 'mo', maxDevices: 3,   label: 'Basic'      },
  education:  { price: 500,  currency: 'KES', period: 'mo', maxDevices: 10,  label: 'Education'  },
  research:   { price: 2500, currency: 'KES', period: 'mo', maxDevices: 999, label: 'Research'   },
  enterprise: { price: null, currency: 'KES', period: 'mo', maxDevices: 999, label: 'Enterprise' },
  custom:     { price: 1000, currency: 'KES', period: 'mo', maxDevices: 999, label: 'Custom'     },
};
const loadTierConfig = () => {
  try { return JSON.parse(localStorage.getItem('anga_tier_config')) || DEFAULT_TIER_CONFIG; }
  catch { return DEFAULT_TIER_CONFIG; }
};

// ── Reusable searchable combobox ──────────────────────────────────────────────
const SearchCombobox = ({ value, onChange, options, placeholder, getLabel, getValue, disabled }) => {
  const [query, setQuery]   = React.useState('');
  const [open,  setOpen]    = React.useState(false);
  const ref                 = React.useRef(null);

  // Keep label in sync when value changes externally
  const selectedLabel = React.useMemo(() => {
    const found = options.find(o => getValue(o) === value);
    return found ? getLabel(found) : '';
  }, [value, options, getValue, getLabel]);

  const [display, setDisplay] = React.useState(selectedLabel);
  React.useEffect(() => { setDisplay(selectedLabel); }, [selectedLabel]);

  const filtered = query.trim()
    ? options.filter(o => getLabel(o).toLowerCase().includes(query.toLowerCase()))
    : options;

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          value={open ? query : display}
          onChange={e => { setQuery(e.target.value); setDisplay(e.target.value); onChange(''); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none pr-8"
        />
        <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9ca3af', fontSize: 11 }}>▼</span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', zIndex: 50, width: '100%', maxHeight: 200,
          overflowY: 'auto', background: '#fff', border: '1px solid #d1d5db',
          borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: 2,
        }}>
          {filtered.length === 0
            ? <div style={{ padding: '8px 12px', fontSize: 12, color: '#9ca3af' }}>No matches</div>
            : filtered.map(o => (
              <div
                key={getValue(o)}
                onMouseDown={() => { onChange(getValue(o)); setDisplay(getLabel(o)); setOpen(false); setQuery(''); }}
                style={{
                  padding: '8px 12px', fontSize: 13, cursor: 'pointer',
                  background: getValue(o) === value ? '#fffbeb' : 'transparent',
                  color: getValue(o) === value ? '#92400e' : '#111827',
                  fontWeight: getValue(o) === value ? 600 : 400,
                }}
                onMouseEnter={e => { if (getValue(o) !== value) e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={e => { e.currentTarget.style.background = getValue(o) === value ? '#fffbeb' : 'transparent'; }}
              >
                {getLabel(o)}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
};

// ── Soil Sensor Edit Modal ────────────────────────────────────────────────────
const EditSoilModal = ({ device, devices, users, onClose, onSave, showToast }) => {
  const [form, setForm] = React.useState({
    name:             device.name || '',
    depth_cm:         device.depth_cm ?? 10,
    colocated:        !!device.weatherStationId,
    weatherStationId: device.weatherStationId || '',
    assignedUserIds:  (device.assignedUsers || []).map(u => u._id || u),
    lat:              device.location?.latitude  != null ? String(device.location.latitude)  : '',
    lng:              device.location?.longitude != null ? String(device.location.longitude) : '',
  });
  const [saving,     setSaving]     = React.useState(false);
  const [userSearch, setUserSearch] = React.useState('');

  const selectedStation = devices.find(s => s.id === form.weatherStationId);

  // When colocated station with GPS is chosen, auto-fill lat/lng
  React.useEffect(() => {
    if (form.colocated && selectedStation?.lat != null) {
      setForm(f => ({ ...f, lat: String(selectedStation.lat), lng: String(selectedStation.lng) }));
    }
  }, [form.colocated, form.weatherStationId, selectedStation]);

  const activeUsers     = users.filter(u => u.isActive && u.emailVerified);
  const filteredUsers   = userSearch.trim()
    ? activeUsers.filter(u => `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(userSearch.toLowerCase()))
    : activeUsers;

  const toggleUser = (uid) =>
    setForm(f => ({
      ...f,
      assignedUserIds: f.assignedUserIds.includes(uid)
        ? f.assignedUserIds.filter(id => id !== uid)
        : [...f.assignedUserIds, uid],
    }));

  const save = async () => {
    setSaving(true);
    try {
      const latNum = parseFloat(form.lat);
      const lngNum = parseFloat(form.lng);
      const patch = {
        name:             form.name.trim() || undefined,
        depth_cm:         Number(form.depth_cm),
        weatherStationId: form.colocated ? form.weatherStationId : null,
        assignedUsers:    form.assignedUserIds,
        location: (!isNaN(latNum) && !isNaN(lngNum))
          ? { latitude: latNum, longitude: lngNum }
          : null,
      };
      await updateSoilDevice(device.deviceId, patch);
      onSave(device.deviceId, patch, users.filter(u => form.assignedUserIds.includes(u.id || u._id)));
      showToast('Soil sensor updated');
      onClose();
    } catch (e) {
      showToast(e.message || 'Save failed', false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Edit Soil Sensor — ${device.name || device.deviceId}`} onClose={onClose}>
      <div className="space-y-5">

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Display Name</label>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder={`Soil ${device.deviceId.slice(-6)}`}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Probe Depth (cm)</label>
          <input type="number" min="1" max="200" value={form.depth_cm}
            onChange={e => setForm(f => ({ ...f, depth_cm: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none" />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-2">Placement</label>
          <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1 w-fit">
            <button onClick={() => setForm(f => ({ ...f, colocated: false, weatherStationId: '' }))}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${!form.colocated ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              Standalone
            </button>
            <button onClick={() => setForm(f => ({ ...f, colocated: true }))}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all ${form.colocated ? 'bg-white text-amber-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              Colocated with Weather Station
            </button>
          </div>

          {form.colocated && (
            <div className="mt-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Search & select weather station</label>
              <SearchCombobox
                value={form.weatherStationId}
                onChange={id => setForm(f => ({ ...f, weatherStationId: id }))}
                options={devices}
                placeholder="Type station name to search…"
                getValue={s => s.id}
                getLabel={s => `${s.name}${s.lat != null ? ` (${s.lat.toFixed(4)}, ${s.lng.toFixed(4)})` : ' (no GPS)'}`}
              />
              {selectedStation?.lat != null && (
                <p className="mt-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-1.5">
                  GPS auto-filled from <strong>{selectedStation.name}</strong>
                </p>
              )}
            </div>
          )}
        </div>

        {/* GPS coordinates — always editable */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            GPS Coordinates
            {form.lat && form.lng
              ? <span className="ml-2 font-normal text-green-600">✓ set</span>
              : <span className="ml-2 font-normal text-red-500">not set — sensor won't appear on map</span>}
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] text-gray-400 mb-0.5">Latitude</label>
              <input
                type="number" step="any" placeholder="-1.2921"
                value={form.lat}
                onChange={e => setForm(f => ({ ...f, lat: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] text-gray-400 mb-0.5">Longitude</label>
              <input
                type="number" step="any" placeholder="36.8219"
                value={form.lng}
                onChange={e => setForm(f => ({ ...f, lng: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none"
              />
            </div>
          </div>
          <p className="mt-1 text-[10px] text-gray-400">
            Tip: right-click any location on Google Maps → copy the coordinates.
          </p>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-2">
            Assigned Users
            <span className="ml-2 text-gray-400 font-normal">({form.assignedUserIds.length} selected)</span>
          </label>
          <div className="relative mb-2">
            <Search style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: '#9ca3af' }} />
            <input
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              placeholder="Search users by name or email…"
              className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-1.5 text-xs focus:ring-2 focus:ring-amber-400 focus:outline-none"
            />
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden max-h-44 overflow-y-auto">
            {filteredUsers.length === 0 && (
              <p className="text-xs text-gray-400 p-3 text-center">
                {userSearch ? 'No users match your search' : 'No active users found'}
              </p>
            )}
            {filteredUsers.map(u => {
              const uid = u.id || u._id;
              const checked = form.assignedUserIds.includes(uid);
              return (
                <label key={uid}
                  className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-0 ${checked ? 'bg-amber-50' : ''}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleUser(uid)} className="accent-amber-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{u.firstName} {u.lastName}</p>
                    <p className="text-xs text-gray-400 truncate">{u.email}</p>
                  </div>
                  {checked && <span className="text-xs text-amber-700 font-semibold flex-shrink-0">Assigned</span>}
                </label>
              );
            })}
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={save} disabled={saving || (form.colocated && !form.weatherStationId)}
            className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const SuperAdminDashboard = ({ userRole = USER_ROLES.SUPER_ADMIN, userData, onLogout, onExit }) => {
  const [nav,            setNav]            = useState('overview');
  const [sidebarOpen,    setSidebarOpen]    = useState(() => window.innerWidth >= 1024);
  const [users,          setUsers]          = useState([]);
  const [userPage,       setUserPage]       = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [pending,        setPending]        = useState([]);
  const [devices,        setDevices]        = useState([]);
  const [soilDevices,    setSoilDevices]    = useState([]);
  const [deviceTypeFilter, setDeviceTypeFilter] = useState('weather');
  const [editSoilModal,  setEditSoilModal]  = useState(null); // { device }
  const [userStats,      setUserStats]      = useState(null);
  const [stationMeta,    setStationMeta]    = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [tabLoading,     setTabLoading]     = useState(false);
  const [search,         setSearch]         = useState('');
  const [toast,          setToast]          = useState(null);
  const [rejectModal,    setRejectModal]    = useState(null);
  const [rejectReason,   setRejectReason]   = useState('');
  const [docModal,       setDocModal]       = useState(null);
  const [deleteModal,    setDeleteModal]    = useState(null);
  const [suspendModal,    setSuspendModal]    = useState(null);
  const [suspendReason,   setSuspendReason]   = useState('');
  const [suspendDuration, setSuspendDuration] = useState('indefinite');
  const [accessModal,    setAccessModal]    = useState(null);
  const [editStation,    setEditStation]    = useState(null);
  const [openMenuId,     setOpenMenuId]     = useState(null);
  const [menuPos,        setMenuPos]        = useState(null);
  const [tierConfig,           setTierConfig]           = useState(loadTierConfig);
  const [tierDraft,            setTierDraft]            = useState(null);
  const [purgeSuspendedModal,  setPurgeSuspendedModal]  = useState(false);
  const [assignCustodianModal, setAssignCustodianModal] = useState(null); // { device }
  const [selectedStation,     setSelectedStation]     = useState(null);
  const [selectedStationInfo, setSelectedStationInfo] = useState(null);

  const handleStationSelect = (stationId, stationInfo) => {
    setSelectedStation(stationId);
    setSelectedStationInfo(stationInfo);
  };
  const [apiKeys,              setApiKeys]              = useState([]);
  const [apiKeyLoading,        setApiKeyLoading]        = useState(false);
  const [newKeySecret,         setNewKeySecret]         = useState(null); // shown once after generation
  const [msgUnread,            setMsgUnread]            = useState(0);
  const [addUserModal,         setAddUserModal]         = useState(false);
  const [darkMode,             setDarkMode]             = useState(() => {
    try { return localStorage.getItem('darkMode') === 'true'; } catch { return false; }
  });
  const [trafficStats,         setTrafficStats]         = useState(null);
  const [trafficLoading,       setTrafficLoading]       = useState(false);
  const [trafficError,         setTrafficError]         = useState(null);

  useEffect(() => {
    try { localStorage.setItem('darkMode', darkMode); } catch { /* ignore */ }
  }, [darkMode]);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  // Open dropdown — record button position so dropdown renders fixed (avoids overflow:hidden clipping)
  const handleOpenMenu = (userId, e) => {
    if (openMenuId === userId) { setOpenMenuId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setOpenMenuId(userId);
  };

  const [devicesLastUpdated, setDevicesLastUpdated] = useState(null);
  const [devicesRefreshing,  setDevicesRefreshing]  = useState(false);

  const loadWeatherDevices = useCallback(async (silent = false) => {
    if (!silent) setDevicesRefreshing(true);
    try {
      const devData = await fetchWeatherDevices();
      setDevices(Array.isArray(devData) ? devData : []);
      setDevicesLastUpdated(new Date());
    } catch { /* keep stale data */ }
    finally { setDevicesRefreshing(false); }
  }, []);

  // ── Initial load ───────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [devData, pendData, statsData] = await Promise.all([
          fetchWeatherDevices().catch(() => []),
          fetchPendingVerifications().catch(() => ({ data: [] })),
          fetchUserStats().catch(() => null),
        ]);
        setDevices(Array.isArray(devData) ? devData : []);
        setDevicesLastUpdated(new Date());
        setPending(Array.isArray(pendData?.data) ? pendData.data : []);
        setUserStats(statsData);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // ── Auto-refresh weather devices every 60 s when on the devices tab ────────
  useEffect(() => {
    if (nav !== 'devices') return;
    const id = setInterval(() => loadWeatherDevices(true), 60_000);
    return () => clearInterval(id);
  }, [nav, loadWeatherDevices]);

  // ── Load per-tab data ──────────────────────────────────────────────────────
  const loadUsers = useCallback(async (page = 1, q = '', pageSize = 15) => {
    setTabLoading(true);
    try {
      const data = await fetchAllUsers(page, pageSize, { q });
      setUsers(Array.isArray(data?.data) ? data.data : []);
      setUserTotalPages(data?.totalPages || 1);
      setUserPage(page);
    } catch { showToast('Failed to load users', false); }
    finally  { setTabLoading(false); }
  }, []);

  const loadApiKeys = useCallback(async () => {
    setApiKeyLoading(true);
    try {
      const res = await fetchAllApiKeys();
      setApiKeys(res.keys || []);
    } catch (e) { showToast(e.message || 'Failed to load API keys', false); }
    finally { setApiKeyLoading(false); }
  }, []);

  useEffect(() => {
    if (nav === 'users')          loadUsers(1, search);
    if (nav === 'access')         loadUsers(1, search);
    if (nav === 'station-names')  getAllDevicesMeta().then(setStationMeta).catch(() => {});
    if (nav === 'api-keys')       { loadApiKeys(); loadUsers(1, '', 200); }
    if (nav === 'devices')        { loadWeatherDevices(); fetchSoilDevices().then(setSoilDevices).catch(() => {}); loadUsers(1, '', 200); }
    if (nav === 'traffic')        {
      setTrafficLoading(true);
      setTrafficError(null);
      fetchTrafficStats()
        .then(setTrafficStats)
        .catch(e => setTrafficError(e.message || 'Failed to load traffic data'))
        .finally(() => setTrafficLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav, loadUsers, loadApiKeys, loadWeatherDevices]);

  // Tick every second so the "X ago" label stays current
  const [, setTick] = useState(0);
  useEffect(() => {
    if (nav !== 'devices') return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [nav]);

  const refreshStats = () => fetchUserStats().then(setUserStats).catch(() => {});

  // ── User actions ───────────────────────────────────────────────────────────
  const handleApprove = async (userId) => {
    try {
      await approveUser(userId);
      setPending(p => p.filter(u => u.id !== userId));
      refreshStats();
      showToast('Account approved — notification sent');
    } catch (e) { showToast(e.message || 'Approval failed', false); }
  };

  const handleRejectSubmit = async () => {
    if (!rejectModal) return;
    try {
      await rejectUser(rejectModal.userId, rejectReason || 'Your verification document could not be verified.');
      setPending(p => p.filter(u => u.id !== rejectModal.userId));
      refreshStats();
      showToast('Account rejected — email sent to user');
    } catch (e) { showToast(e.message || 'Rejection failed', false); }
    finally { setRejectModal(null); setRejectReason(''); }
  };

  const handleSuspend = async () => {
    if (!suspendModal) return;
    try {
      const durationDays = { '1d': 1, '3d': 3, '7d': 7, '30d': 30 };
      const days = durationDays[suspendDuration];
      const suspendedUntil = days ? new Date(Date.now() + days * 86400000).toISOString() : null;
      await suspendUser(suspendModal.userId, suspendReason, suspendedUntil);
      setUsers(u => u.map(x => x.id === suspendModal.userId ? { ...x, isActive: false, suspendedUntil } : x));
      const untilLabel = suspendedUntil ? ` until ${new Date(suspendedUntil).toLocaleDateString()}` : ' indefinitely';
      showToast(`${suspendModal.name} suspended${untilLabel}`);
    } catch (e) { showToast(e.message || 'Suspend failed', false); }
    finally { setSuspendModal(null); setSuspendReason(''); setSuspendDuration('indefinite'); }
  };

  const handleActivate = async (userId) => {
    try {
      await activateUser(userId);
      setUsers(u => u.map(x => x.id === userId ? { ...x, isActive: true } : x));
      showToast('Account reactivated');
    } catch (e) { showToast(e.message || 'Failed', false); }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      await deleteUser(deleteModal.userId);
      setUsers(u => u.filter(x => x.id !== deleteModal.userId));
      refreshStats();
      showToast(`${deleteModal.name} deleted`);
    } catch (e) { showToast(e.message || 'Delete failed', false); }
    finally { setDeleteModal(null); }
  };

  const handleResendOtp = async (userId, email) => {
    try {
      const res = await adminResendOtp(userId);
      showToast(`OTP resent to ${email}${res.devOtp ? ` — DEV code: ${res.devOtp}` : ''}`);
    } catch (e) { showToast(e.message || 'Failed to resend OTP', false); }
  };

  const handleForceVerify = async (userId, email, accountType) => {
    try {
      await adminForceVerify(userId);
      // Update users list state
      setUsers(u => u.map(x => x.id === userId
        ? { ...x, emailVerified: true, ...(accountType === 'regular' && { isActive: true, isApproved: true }) }
        : x
      ));
      // For students/orgs: reload pending list so they appear for approval
      if (accountType === 'student' || accountType === 'organization') {
        fetchPendingVerifications().then(r => setPending(Array.isArray(r?.data) ? r.data : [])).catch(() => {});
      }
      showToast(`Email verified for ${email} — ${accountType === 'regular' ? 'account is now active' : 'ready for admin approval'}`);
    } catch (e) { showToast(e.message || 'Force verify failed', false); }
  };

  const handlePurgeSuspended = async () => {
    try {
      const res = await deleteAllSuspended();
      setUsers(u => u.filter(x => x.isActive !== false));
      refreshStats();
      showToast(`${res.deletedCount ?? 0} suspended account(s) permanently deleted`);
    } catch (e) { showToast(e.message || 'Purge failed', false); }
    finally { setPurgeSuspendedModal(false); }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await updateUserRole(userId, role);
      setUsers(u => u.map(x => x.id === userId ? { ...x, role } : x));
      showToast(`Role updated to ${getRoleDisplayName(role)}`);
      // When promoted to custodian, open access modal to assign their station immediately
      if (role === 'custodian') {
        const user = users.find(u => u.id === userId);
        if (user) setAccessModal({ user: { ...user, role: 'custodian' }, selectedDevices: user.assignedDevices || [], tier: 'custodian' });
      }
    } catch (e) { showToast(e.message || 'Role update failed', false); }
  };

  const handleSaveAccess = async () => {
    if (!accessModal) return;
    try {
      const { user, selectedDevices, tier } = accessModal;
      // Custodians always get free 'custodian' tier — their assigned station(s) are their reward
      const finalTier = user.role === 'custodian' ? 'custodian' : tier;
      let finalDevices = selectedDevices;
      if (finalTier === 'basic') finalDevices = devices.slice(0, 3).map(d => d.id);
      if ((finalTier === 'research' || finalTier === 'enterprise') && selectedDevices.length === 0) {
        finalDevices = devices.map(d => d.id);
      }
      await assignDevicesToUser(user.id, finalDevices);
      await updateUser(user.id, { accessTier: finalTier });
      setUsers(u => u.map(x => x.id === user.id ? { ...x, assignedDevices: finalDevices, accessTier: finalTier } : x));
      setAccessModal(null);
      showToast('Access updated');
    } catch (e) { showToast(e.message || 'Failed', false); }
  };

  const handleGenerateKey = async (userId, userName) => {
    try {
      const res = await generateApiKeyForUser(userId, `${userName} API Key`);
      setNewKeySecret({ key: res.key, name: res.name, deviceIds: res.deviceIds });
      loadApiKeys();
      showToast('API key generated — save it now!');
    } catch (e) { showToast(e.message || 'Generation failed', false); }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await revokeApiKey(keyId);
      setApiKeys(k => k.map(x => x.id === keyId ? { ...x, isActive: false } : x));
      showToast('API key revoked');
    } catch (e) { showToast(e.message || 'Revoke failed', false); }
  };

  const handleAssignCustodian = async (device, userId) => {
    if (!userId) return;
    try {
      const user = users.find(u => u.id === userId);
      if (!user) return;
      // Promote to custodian role if not already
      if (user.role !== 'custodian') {
        await updateUserRole(userId, 'custodian');
      }
      // Assign the device and set custodian tier (free)
      const existing = user.assignedDevices || [];
      const merged = [...new Set([...existing, device.id])];
      await assignDevicesToUser(userId, merged);
      await updateUser(userId, { accessTier: 'custodian' });
      setUsers(u => u.map(x => x.id === userId ? { ...x, role: 'custodian', assignedDevices: merged, accessTier: 'custodian' } : x));
      setAssignCustodianModal(null);
      showToast(`${user.firstName} ${user.lastName} assigned as custodian of ${device.name}`);
    } catch (e) { showToast(e.message || 'Assignment failed', false); }
  };

  const handleSaveStation = async (meta) => {
    try {
      await saveDeviceMeta(meta.deviceId, { name: meta.name, location: meta.location });
      setStationMeta(s => s.map(x => x.deviceId === meta.deviceId ? meta : x));
      setEditStation(null);
      showToast('Station updated');
    } catch (e) { showToast(e.message || 'Save failed', false); }
  };

  const handleResetStation = async (deviceId) => {
    try {
      await resetDeviceMeta(deviceId);
      setStationMeta(s => s.filter(x => x.deviceId !== deviceId));
      showToast('Station name reset to default');
    } catch (e) { showToast(e.message || 'Reset failed', false); }
  };

  // ── Derived data ───────────────────────────────────────────────────────────
  // Build gateway map — use the device's lastSeen as proxy for the gateway's last activity
  // (the gateway object in a reading has no lastSeen; if a device reported through it, the gateway was up then)
  const gatewayMap = {};
  devices.forEach(d => {
    const gws = d.allGateways?.length
      ? d.allGateways
      : (d.gatewayId ? [{ gatewayId: d.gatewayId, rssi: d.rssi }] : []);
    gws.forEach(g => {
      if (!g.gatewayId) return;
      if (!gatewayMap[g.gatewayId]) {
        gatewayMap[g.gatewayId] = { id: g.gatewayId, devicesReached: 0, rssiSum: 0, rssiCount: 0, lastSeen: null };
      }
      gatewayMap[g.gatewayId].devicesReached++;
      if (g.rssi != null) { gatewayMap[g.gatewayId].rssiSum += g.rssi; gatewayMap[g.gatewayId].rssiCount++; }
      // Use the device's lastSeen — the most recent device report via this gateway = gateway was live then
      const devLastSeen = d.timestamp || d.lastSeen;
      if (devLastSeen && (!gatewayMap[g.gatewayId].lastSeen || devLastSeen > gatewayMap[g.gatewayId].lastSeen)) {
        gatewayMap[g.gatewayId].lastSeen = devLastSeen;
      }
    });
  });
  const gatewayList = Object.values(gatewayMap).map(g => ({
    ...g,
    avgRssi:   g.rssiCount ? +(g.rssiSum / g.rssiCount).toFixed(1) : null,
    shortName: fmtGw(g.id),
    health:    gwHealth(g.lastSeen),
  })).sort((a, b) => b.devicesReached - a.devicesReached);

  const online  = devices.filter(d => d.status === 'online').length;
  const warning = devices.filter(d => d.status === 'warning').length;
  const offline = devices.filter(d => d.status === 'offline').length;

  // Map deviceId → custodian user (for the Devices table)
  const custodiansByDevice = {};
  users.filter(u => u.role === 'custodian').forEach(u => {
    (u.assignedDevices || []).forEach(deviceId => {
      custodiansByDevice[deviceId] = u;
    });
  });

  const q = search.toLowerCase();
  const filteredPending = pending.filter(u =>
    `${u.firstName} ${u.lastName} ${u.email} ${u.institution || ''}`.toLowerCase().includes(q)
  );
  const filteredUsers = users.filter(u =>
    `${u.firstName} ${u.lastName} ${u.email} ${u.username || ''}`.toLowerCase().includes(q)
  );

  // ── NAV items ──────────────────────────────────────────────────────────────
  const NAV = [
    { key: 'overview',      label: 'Overview',         icon: LayoutDashboard },
    { key: 'pending',       label: 'Pending',           icon: Clock,           badge: pending.length },
    { key: 'users',         label: 'Users',             icon: Users },
    { key: 'access',        label: 'Access Control',    icon: Shield },
    { key: 'devices',       label: 'Devices',           icon: Wifi },
    { key: 'gateways',      label: 'Gateways',          icon: Radio },
    { key: 'station-names', label: 'Station Names',     icon: MapPin },
    { key: 'messages',      label: 'Messages',          icon: MessageSquare,   badge: msgUnread },
    { key: 'custodians',    label: 'Custodians',        icon: UserCheck },
    { key: 'map',           label: 'Live Map',          icon: Map },
    { key: 'api-keys',      label: 'API Keys',          icon: Key },
    { key: 'tier-config',   label: 'Tier Pricing',      icon: DollarSign },
    { key: 'settings',      label: 'Settings',          icon: Settings },
    { key: 'traffic',       label: 'Site Traffic',      icon: BarChart2 },
    { key: 'logs',          label: 'System Logs',       icon: ScrollText },
  ];

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center">
        <RefreshCw className="w-10 h-10 text-purple-500 animate-spin mx-auto" />
        <p className="mt-3 text-gray-500 font-medium">Loading dashboard…</p>
      </div>
    </div>
  );

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">

      {/* Mobile overlay backdrop */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <aside className={`
        bg-white border-r border-gray-200 flex flex-col shrink-0 z-40
        fixed lg:relative inset-y-0 left-0 h-full
        transition-transform lg:transition-all duration-200
        ${sidebarOpen ? 'translate-x-0 w-56' : '-translate-x-full lg:translate-x-0 lg:w-14'}
      `}>
        {/* Brand */}
        <div className={`flex items-center gap-3 px-4 py-4 border-b border-gray-100 ${!sidebarOpen && 'justify-center'}`}>
          <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center shrink-0">
            <Shield className="w-4 h-4 text-white" />
          </div>
          {sidebarOpen && <span className="font-bold text-gray-900 text-sm leading-tight">Climate Pulse Admin</span>}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 space-y-0.5 px-2">
          {NAV.map(({ key, label, icon: Icon, badge }) => {
            const active = nav === key;
            return (
              <button key={key} onClick={() => { setNav(key); setSearch(''); setSelectedStation(null); setSelectedStationInfo(null); }}
                title={!sidebarOpen ? label : undefined}
                className={`w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-sm font-medium transition-colors relative
                  ${active ? 'bg-purple-50 text-purple-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
                <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-purple-600' : ''}`} />
                {sidebarOpen && <span className="flex-1 text-left">{label}</span>}
                {sidebarOpen && badge > 0 && (
                  <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{badge}</span>
                )}
                {!sidebarOpen && badge > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-orange-500 rounded-full" />
                )}
              </button>
            );
          })}
        </nav>

        {/* User info + Notifications + Logout + Collapse */}
        <div className="border-t border-gray-100 p-2 space-y-0.5">
          {/* Notification bell — dark sidebar style */}
          <div style={{ background: '#1e293b', borderRadius: 8, marginBottom: 2 }}>
            <NotificationBell collapsed={!sidebarOpen} onNavigate={(v) => setNav(v)} />
          </div>

          {/* User card → profile */}
          {userData && sidebarOpen && (
            <button
              onClick={() => setNav('profile')}
              className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-colors ${nav === 'profile' ? 'bg-amber-50 text-amber-700' : 'hover:bg-gray-50 text-gray-700'}`}
            >
              <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-xs shrink-0">
                {(userData.firstName?.[0] || userData.email?.[0] || '?').toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-gray-800 truncate">
                  {userData.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : userData.email}
                </p>
                <p className="text-[10px] text-gray-400 truncate">Settings & profile</p>
              </div>
              <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            </button>
          )}
          {userData && !sidebarOpen && (
            <button
              onClick={() => setNav('profile')}
              title="Profile"
              className="w-full flex justify-center py-1"
            >
              <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-xs">
                {(userData.firstName?.[0] || '?').toUpperCase()}
              </div>
            </button>
          )}
          {onLogout && (
            <button onClick={onLogout}
              title={!sidebarOpen ? 'Logout' : undefined}
              className={`w-full flex items-center gap-2 px-2.5 py-2 text-xs text-red-500 hover:text-red-700 rounded-lg hover:bg-red-50 ${!sidebarOpen && 'justify-center'}`}>
              <LogOut className="w-4 h-4 shrink-0" />
              {sidebarOpen && <span>Logout</span>}
            </button>
          )}
          {onExit && (
            <button onClick={onExit}
              title={!sidebarOpen ? 'Back to App' : undefined}
              className={`w-full flex items-center gap-2 px-2.5 py-2 text-xs text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-50 ${!sidebarOpen && 'justify-center'}`}>
              <ChevronLeft className="w-4 h-4 shrink-0" />
              {sidebarOpen && <span>Back to App</span>}
            </button>
          )}
          <button onClick={() => setSidebarOpen(o => !o)}
            className="w-full flex items-center justify-center gap-2 py-2 text-xs text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50">
            {sidebarOpen ? <><ChevronLeft className="w-4 h-4" /><span>Collapse</span></> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </aside>

      {/* ── Main ─────────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto w-full min-w-0">

        {/* Top bar */}
        <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 lg:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={() => setSidebarOpen(o => !o)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div>
              <h1 className="font-bold text-gray-900 text-sm lg:text-base">{NAV.find(n => n.key === nav)?.label}</h1>
              <p className="text-xs text-gray-400 hidden sm:block">Anga Weather Network · SuperAdmin</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Quick stats pills */}
            <span className="hidden md:flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-semibold">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>{online} online
            </span>
            {pending.length > 0 && (
              <button onClick={() => setNav('pending')}
                className="flex items-center gap-1 text-xs bg-orange-50 text-orange-700 px-2.5 py-1 rounded-full font-semibold hover:bg-orange-100">
                <Clock className="w-3 h-3" />{pending.length} pending
              </button>
            )}
            <button onClick={() => window.location.reload()}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white
            ${toast.ok ? 'bg-green-600' : 'bg-red-600'}`}>
            {toast.ok ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {toast.msg}
          </div>
        )}

        {/* Content */}
        <div className="p-4 lg:p-6 space-y-4 lg:space-y-6">

          {/* ══ OVERVIEW ══════════════════════════════════════════════ */}
          {nav === 'overview' && (
            <div className="space-y-6">
              {/* Network KPIs */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard label="Total Stations" value={devices.length}  icon={<Wifi    className="w-5 h-5 text-blue-600"   />} color="blue"   />
                <KpiCard label="Online"          value={online}         icon={<Activity className="w-5 h-5 text-green-600" />} color="green"  sub={`${Math.round(online/Math.max(devices.length,1)*100)}% uptime`} />
                <KpiCard label="Warning"         value={warning}        icon={<AlertTriangle className="w-5 h-5 text-yellow-600" />} color="yellow" />
                <KpiCard label="Offline"         value={offline}        icon={<WifiOff  className="w-5 h-5 text-red-600"    />} color="red"    />
              </div>
              {/* User KPIs */}
              {userStats && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <KpiCard label="Total Users"     value={userStats.total}           icon={<Users       className="w-5 h-5 text-blue-600"   />} color="blue"   />
                  <KpiCard label="Active"          value={userStats.active}          icon={<CheckCircle className="w-5 h-5 text-green-600"  />} color="green"  />
                  <KpiCard label="Pending Approval" value={userStats.pendingApproval} icon={<Clock       className="w-5 h-5 text-orange-600" />} color="yellow" />
                  <KpiCard label="Gateways"        value={gatewayList.length}        icon={<Radio       className="w-5 h-5 text-purple-600" />} color="purple" />
                </div>
              )}

              {/* Charts row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-800 mb-4">Station Status</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={[{ name:'Online',value:online },{ name:'Warning',value:warning },{ name:'Offline',value:offline }]}
                        cx="50%" cy="50%" outerRadius={75} dataKey="value"
                        label={({ name, value }) => value > 0 ? `${name} ${value}` : ''}>
                        {PIE_COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                      </Pie>
                      <Tooltip /><Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-800 mb-4">Gateway Network</h3>
                  <div className="space-y-3 max-h-[200px] overflow-y-auto pr-1">
                    {gatewayList.map(gw => (
                      <div key={gw.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${gw.health.dot}`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-800 truncate">{gw.shortName}</p>
                          <p className="text-xs text-gray-400">{gw.devicesReached} devices · {timeAgo(gw.lastSeen)}</p>
                        </div>
                        <Badge color={gw.health.color === 'green' ? 'green' : gw.health.color === 'yellow' ? 'yellow' : 'red'}>
                          {gw.health.label}
                        </Badge>
                      </div>
                    ))}
                    {gatewayList.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No gateway data</p>}
                  </div>
                </div>
              </div>

              {/* Pending alert */}
              {pending.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-orange-800 font-medium">
                    <Clock className="w-5 h-5" />
                    {pending.length} account{pending.length !== 1 ? 's' : ''} awaiting document verification
                  </div>
                  <button onClick={() => setNav('pending')}
                    className="text-sm bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 font-medium">
                    Review Now →
                  </button>
                </div>
              )}

              {/* Account type breakdown */}
              {userStats?.byAccountType && (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-800 mb-4">User Account Types</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { key:'regular',      label:'Regular',      icon:<Users          className="w-4 h-4 text-gray-500"   />, color:'gray'   },
                      { key:'student',      label:'Students',     icon:<GraduationCap  className="w-4 h-4 text-blue-500"   />, color:'blue'   },
                      { key:'organization', label:'Organizations',icon:<Building       className="w-4 h-4 text-purple-500" />, color:'purple' },
                    ].map(({ key, label, icon, color }) => (
                      <div key={key} className={`rounded-lg p-4 flex items-center gap-3 ${
                        color==='blue'?'bg-blue-50':color==='purple'?'bg-purple-50':'bg-gray-50'}`}>
                        {icon}
                        <div>
                          <p className="text-xs text-gray-500">{label}</p>
                          <p className="text-xl font-bold text-gray-900">{userStats.byAccountType[key] ?? 0}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══ PENDING APPROVALS ══════════════════════════════════════ */}
          {nav === 'pending' && (
            <div className="space-y-4">
              <SectionHeader
                title="Pending Verifications"
                sub="Students and organizations awaiting document review"
                actions={
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
                      className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none" />
                  </div>
                }
              />
              {filteredPending.length === 0
                ? <EmptyState icon={CheckCircle} title="All clear" sub="No accounts pending verification" />
                : filteredPending.map(user => {
                  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                  const isStudent = user.accountType === 'student';
                  const isOrg     = user.accountType === 'organization';
                  return (
                    <div key={user.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-sm transition-shadow">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        {/* Identity */}
                        <div className="flex items-start gap-4 flex-1 min-w-0">
                          <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
                            isStudent ? 'bg-blue-100' : isOrg ? 'bg-purple-100' : 'bg-gray-100'}`}>
                            {isStudent ? <GraduationCap className="w-5 h-5 text-blue-600" />
                              : isOrg  ? <Building      className="w-5 h-5 text-purple-600" />
                              :          <Users         className="w-5 h-5 text-gray-500" />}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-bold text-gray-900">{name}</p>
                              <Badge color={isStudent ? 'blue' : 'purple'}>
                                {isStudent ? 'Student' : 'Organization'}
                              </Badge>
                              {/* Email verified indicator */}
                              <Badge color={user.emailVerified ? 'green' : 'red'}>
                                {user.emailVerified
                                  ? <><CheckCircle className="w-3 h-3" /> OTP Verified</>
                                  : <><XCircle className="w-3 h-3" /> Email Not Verified</>}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5">{user.email}</p>
                            <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                              {user.institution     && <span className="flex items-center gap-1"><Building className="w-3 h-3" />{user.institution}</span>}
                              {user.organizationName && <span className="flex items-center gap-1"><Building className="w-3 h-3" />{user.organizationName}</span>}
                              {user.studentId       && <span className="flex items-center gap-1"><FileText className="w-3 h-3" />ID: {user.studentId}</span>}
                              {user.course          && <span>{user.course}, Year {user.yearOfStudy}</span>}
                              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(user.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        {/* Actions */}
                        <div className="flex items-center gap-2 shrink-0 flex-wrap">
                          {!user.emailVerified && (
                            <>
                              <button onClick={() => handleResendOtp(user.id, user.email)}
                                className="flex items-center gap-1.5 text-sm px-3 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 font-medium">
                                <Send className="w-4 h-4" />Resend OTP
                              </button>
                              <button onClick={() => handleForceVerify(user.id, user.email, user.accountType)}
                                className="flex items-center gap-1.5 text-sm px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 font-medium"
                                title="Manually verify email without OTP — use when customer cannot receive OTP">
                                <UserCheck className="w-4 h-4" />Verify Email
                              </button>
                            </>
                          )}
                          {user.hasVerificationDoc && (
                            <button onClick={() => setDocModal({ userId: user.id, name })}
                              className="flex items-center gap-1.5 text-sm px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">
                              <Eye className="w-4 h-4" />View Doc
                            </button>
                          )}
                          <button onClick={() => { setRejectModal({ userId: user.id, name }); }}
                            className="flex items-center gap-1.5 text-sm px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 font-medium">
                            <XCircle className="w-4 h-4" />Reject
                          </button>
                          <button onClick={() => handleApprove(user.id)}
                            disabled={!user.emailVerified}
                            title={!user.emailVerified ? 'User must verify email first' : ''}
                            className="flex items-center gap-1.5 text-sm px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                            <CheckCircle className="w-4 h-4" />Approve
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}

          {/* ══ USERS ══════════════════════════════════════════════════ */}
          {nav === 'users' && (
            <div className="space-y-4">
              <SectionHeader
                title="All Users"
                sub={`${userStats?.total ?? users.length} registered accounts`}
                actions={
                  <div className="flex items-center gap-2">
                    {users.some(u => !u.isActive) && (
                      <button onClick={() => setPurgeSuspendedModal(true)}
                        className="flex items-center gap-1.5 text-xs px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 font-medium">
                        <AlertOctagon className="w-3.5 h-3.5" />
                        Purge Suspended ({users.filter(u => !u.isActive).length})
                      </button>
                    )}
                    <button onClick={() => setAddUserModal(true)}
                      className="flex items-center gap-1.5 text-xs px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">
                      <Plus className="w-3.5 h-3.5" />
                      Add User
                    </button>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input value={search} onChange={e => { setSearch(e.target.value); loadUsers(1, e.target.value); }}
                        placeholder="Search users…"
                        className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none w-52" />
                    </div>
                  </div>
                }
              />

              {tabLoading
                ? <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 text-purple-400 animate-spin" /></div>
                : (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden overflow-x-auto">
                  <table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-5 py-3 font-semibold text-gray-600">User</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">Status</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden lg:table-cell">Role</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden xl:table-cell">Joined</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredUsers.map(user => {
                        const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username;
                        return (
                          <tr key={user.id} className={`hover:bg-gray-50 ${!user.isActive ? 'opacity-60' : ''}`}>
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-xs shrink-0">
                                  {(user.firstName?.[0] || user.username?.[0] || '?').toUpperCase()}
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900">{name}</p>
                                  <p className="text-gray-400 text-xs">{user.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3.5 hidden md:table-cell">
                              <div className="flex flex-col gap-1">
                                <Badge color={user.isActive ? 'green' : 'red'}>
                                  {user.isActive ? 'Active' : 'Suspended'}
                                </Badge>
                                <Badge color={user.emailVerified ? 'green' : 'yellow'}>
                                  {user.emailVerified
                                    ? <><CheckCircle className="w-3 h-3" />Email Verified</>
                                    : <><Clock className="w-3 h-3" />Pending OTP</>}
                                </Badge>
                              </div>
                            </td>
                            <td className="px-4 py-3.5 hidden lg:table-cell">
                              <select value={user.role || 'user'} onChange={e => handleRoleChange(user.id, e.target.value)}
                                className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white focus:ring-2 focus:ring-purple-400 focus:outline-none">
                                {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                              </select>
                            </td>
                            <td className="px-4 py-3.5 hidden xl:table-cell text-gray-400 text-xs">
                              {new Date(user.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3.5">
                              <div className="flex items-center gap-1 justify-end">
                                <button onClick={e => handleOpenMenu(user.id, e)}
                                  className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100">
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredUsers.length === 0 && !tabLoading && (
                    <EmptyState icon={Users} title="No users found" sub="Try a different search term" />
                  )}
                  {/* Pagination */}
                  {userTotalPages > 1 && (
                    <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
                      <p className="text-xs text-gray-400">Page {userPage} of {userTotalPages}</p>
                      <div className="flex gap-2">
                        <button disabled={userPage <= 1} onClick={() => loadUsers(userPage - 1, search)}
                          className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                          <ChevronLeft className="w-3 h-3" />
                        </button>
                        <button disabled={userPage >= userTotalPages} onClick={() => loadUsers(userPage + 1, search)}
                          className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ══ ACCESS CONTROL ═════════════════════════════════════════ */}
          {nav === 'access' && (
            <div className="space-y-5">
              <SectionHeader
                title="Access Control"
                sub="Assign which weather stations each user can view and download data from"
              />

              {/* Tier cards — prices and limits driven by tierConfig */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
                {ACCESS_TIERS.map(t => {
                  const cfg = tierConfig[t.value] || {};
                  const priceLabel = cfg.price == null
                    ? 'Custom'
                    : cfg.price === 0
                    ? 'Free'
                    : `KES ${Number(cfg.price).toLocaleString()}/mo`;
                  const limitLabel = (!cfg.maxDevices || cfg.maxDevices >= 999)
                    ? 'All stations'
                    : `Up to ${cfg.maxDevices} stations`;
                  const bg = { gray:'bg-gray-50 border-gray-200', blue:'bg-blue-50 border-blue-200', purple:'bg-purple-50 border-purple-200', green:'bg-green-50 border-green-200', orange:'bg-orange-50 border-orange-200' };
                  const badgeColor = { gray:'gray', blue:'blue', purple:'purple', green:'green', orange:'orange' };
                  return (
                    <div key={t.value} className={`rounded-xl border p-4 ${bg[t.color] || bg.gray}`}>
                      <div className="flex items-start justify-between mb-1">
                        <p className="font-bold text-gray-900">{t.label}</p>
                        <Badge color={badgeColor[t.color]}>{priceLabel}</Badge>
                      </div>
                      <p className="text-[11px] text-gray-400 mb-2">{limitLabel}</p>
                      <p className="text-xs text-gray-500 mb-2">{t.badge}</p>
                      <ul className="space-y-1">
                        {t.features.map(f => (
                          <li key={f} className="flex items-start gap-1.5 text-xs text-gray-600">
                            <CheckCircle className="w-3 h-3 text-green-500 shrink-0 mt-0.5" />{f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>

              {tabLoading
                ? <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 text-purple-400 animate-spin" /></div>
                : (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden overflow-x-auto">
                  <table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-5 py-3 font-semibold text-gray-600">User</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Tier</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">Stations Assigned</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredUsers.map(user => {
                        const name = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username;
                        const tier = user.accessTier || 'basic';
                        const tierInfo = ACCESS_TIERS.find(t => t.value === tier) || ACCESS_TIERS[0];
                        return (
                          <tr key={user.id} className="hover:bg-gray-50">
                            <td className="px-5 py-3.5">
                              <p className="font-semibold text-gray-900">{name}</p>
                              <p className="text-gray-400 text-xs">{user.email}</p>
                            </td>
                            <td className="px-4 py-3.5">
                              <Badge color={tierInfo.color || 'gray'}>
                                {tierInfo.label}
                              </Badge>
                            </td>
                            <td className="px-4 py-3.5 hidden md:table-cell text-gray-500 text-xs">
                              {tier === 'research' || tier === 'enterprise'
                                ? `All ${devices.length} stations`
                                : tier === 'education'
                                ? `Assigned + 5 nearby`
                                : `${(user.assignedDevices || []).length} of ${devices.length}`}
                            </td>
                            <td className="px-4 py-3.5 text-right">
                              <button onClick={() => setAccessModal({ user, selectedDevices: user.assignedDevices || [], tier })}
                                className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">
                                Edit Access
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ══ DEVICES ════════════════════════════════════════════════ */}
          {nav === 'devices' && (
            <div className="space-y-4">
              <SectionHeader
                title={deviceTypeFilter === 'weather' ? 'Weather Stations' : 'Soil Sensors'}
                sub={deviceTypeFilter === 'weather'
                  ? `${devices.length} stations in the network`
                  : `${soilDevices.length} soil sensors in the network`}
                actions={
                  <div className="flex items-center gap-3">
                    {/* Type dropdown */}
                    <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1">
                      <button
                        onClick={() => setDeviceTypeFilter('weather')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                          deviceTypeFilter === 'weather'
                            ? 'bg-white text-blue-700 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Radio className="w-3.5 h-3.5" />
                        Weather Stations
                        <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                          deviceTypeFilter === 'weather' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'
                        }`}>{devices.length}</span>
                      </button>
                      <button
                        onClick={() => setDeviceTypeFilter('soil')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                          deviceTypeFilter === 'soil'
                            ? 'bg-white text-amber-700 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Sprout className="w-3.5 h-3.5" />
                        Soil Sensors
                        <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                          deviceTypeFilter === 'soil' ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-500'
                        }`}>{soilDevices.length}</span>
                      </button>
                    </div>
                    {/* Search */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input value={search} onChange={e => setSearch(e.target.value)}
                        placeholder={deviceTypeFilter === 'weather' ? 'Search stations…' : 'Search soil sensors…'}
                        className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none" />
                    </div>
                    {/* Live refresh indicator */}
                    {deviceTypeFilter === 'weather' && (
                      <div className="flex items-center gap-2 text-xs text-gray-500 whitespace-nowrap">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block" />
                        {devicesLastUpdated && (
                          <span>Updated {Math.round((Date.now() - devicesLastUpdated) / 1000)}s ago</span>
                        )}
                        <button
                          onClick={() => loadWeatherDevices()}
                          disabled={devicesRefreshing}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${devicesRefreshing ? 'animate-spin' : ''}`} />
                          Refresh
                        </button>
                      </div>
                    )}
                  </div>
                }
              />

              {/* Weather Stations table */}
              {deviceTypeFilter === 'weather' && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden overflow-x-auto">
                  <table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-5 py-3 font-semibold text-gray-600">Station</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">Location</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden lg:table-cell">Last Seen</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden xl:table-cell">Custodian</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {devices.filter(d => !search || d.name?.toLowerCase().includes(q) || d.location?.toLowerCase().includes(q)).map(d => {
                        const custodian = custodiansByDevice[d.id];
                        return (
                        <tr key={d.id} className="hover:bg-gray-50">
                          <td className="px-5 py-3.5">
                            <p className="font-medium text-gray-900">{d.name}</p>
                            <p className="text-gray-400 text-xs font-mono">{d.id?.slice(-10)}</p>
                          </td>
                          <td className="px-4 py-3.5 hidden md:table-cell text-gray-500 text-xs">{d.location || '—'}</td>
                          <td className="px-4 py-3.5">
                            <Badge color={d.status==='online'?'green':d.status==='warning'?'yellow':'red'}>
                              <span className={`w-1.5 h-1.5 rounded-full ${d.status==='online'?'bg-green-500':d.status==='warning'?'bg-yellow-500':'bg-red-500'}`} />
                              {d.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3.5 hidden lg:table-cell text-gray-400 text-xs">{timeAgo(d.timestamp || d.lastSeen)}</td>
                          <td className="px-4 py-3.5 hidden xl:table-cell">
                            {custodian
                              ? <div>
                                  <p className="text-xs font-medium text-green-700">{custodian.firstName} {custodian.lastName}</p>
                                  <p className="text-[10px] text-gray-400">{custodian.email}</p>
                                </div>
                              : <span className="text-xs text-gray-400 italic">Unassigned</span>}
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            <button onClick={() => setAssignCustodianModal({ device: d, currentCustodian: custodian })}
                              className="text-xs px-2.5 py-1 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 whitespace-nowrap">
                              {custodian ? 'Change' : 'Assign Custodian'}
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Soil Sensors table */}
              {deviceTypeFilter === 'soil' && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden overflow-x-auto">
                  <table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-amber-50 border-b border-amber-100">
                      <tr>
                        <th className="text-left px-5 py-3 font-semibold text-amber-800">Device</th>
                        <th className="text-left px-4 py-3 font-semibold text-amber-800 hidden md:table-cell">Depth</th>
                        <th className="text-left px-4 py-3 font-semibold text-amber-800 hidden lg:table-cell">Coordinates</th>
                        <th className="text-left px-4 py-3 font-semibold text-amber-800">Status</th>
                        <th className="text-left px-4 py-3 font-semibold text-amber-800 hidden lg:table-cell">Last Seen</th>
                        <th className="text-left px-4 py-3 font-semibold text-amber-800 hidden xl:table-cell">Colocated Station</th>
                        <th className="text-left px-4 py-3 font-semibold text-amber-800 hidden xl:table-cell">Latest Reading</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {soilDevices.filter(d => !search ||
                        d.deviceId?.toLowerCase().includes(q) ||
                        d.name?.toLowerCase().includes(q) ||
                        d.weatherStationId?.toLowerCase().includes(q)
                      ).map(d => {
                        const r = d.latestReading?.soilData || {};
                        const lat = d.location?.latitude;
                        const lng = d.location?.longitude;
                        return (
                          <tr key={d.deviceId} className="hover:bg-amber-50/40">
                            <td className="px-5 py-3.5">
                              <div className="flex items-center gap-2">
                                <Sprout className="w-5 h-5 text-amber-700 flex-shrink-0" />
                                <div>
                                  <p className="font-medium text-gray-900">{d.name || d.deviceId}</p>
                                  <p className="text-gray-400 text-xs font-mono">{d.deviceId}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3.5 hidden md:table-cell text-gray-500 text-xs">
                              {d.depth_cm != null ? `${d.depth_cm} cm` : '—'}
                            </td>
                            <td className="px-4 py-3.5 hidden lg:table-cell text-gray-400 text-xs font-mono">
                              {lat != null ? `${lat.toFixed(4)}, ${lng.toFixed(4)}` : '—'}
                            </td>
                            <td className="px-4 py-3.5">
                              <Badge color={d.status === 'active' ? 'green' : 'red'}>
                                <span className={`w-1.5 h-1.5 rounded-full ${d.status === 'active' ? 'bg-green-500' : 'bg-red-500'}`} />
                                {d.status === 'active' ? 'Active' : 'Offline'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3.5 hidden lg:table-cell text-gray-400 text-xs">
                              {timeAgo(d.lastSeen)}
                            </td>
                            <td className="px-4 py-3.5 hidden xl:table-cell text-gray-500 text-xs">
                              {d.weatherStationId
                                ? <span className="text-blue-600 font-mono">{d.weatherStationId}</span>
                                : <span className="text-gray-300 italic">Standalone</span>}
                            </td>
                            <td className="px-4 py-3.5 hidden xl:table-cell">
                              {Object.keys(r).length > 0
                                ? <div className="flex flex-wrap gap-1.5">
                                    {r.hum  != null && <span className="inline-flex items-center gap-0.5 text-[10px] bg-blue-50  text-blue-700  px-1.5 py-0.5 rounded font-medium"><Droplets className="w-2.5 h-2.5" />{r.hum?.toFixed(0)}%</span>}
                                    {r.temp != null && <span className="inline-flex items-center gap-0.5 text-[10px] bg-red-50   text-red-700   px-1.5 py-0.5 rounded font-medium"><Thermometer className="w-2.5 h-2.5" />{r.temp?.toFixed(1)}°C</span>}
                                    {r.ph   != null && <span className="inline-flex items-center gap-0.5 text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded font-medium"><FlaskConical className="w-2.5 h-2.5" />pH {r.ph?.toFixed(1)}</span>}
                                    {r.ec   != null && <span className="inline-flex items-center gap-0.5 text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium"><Zap className="w-2.5 h-2.5" />EC {r.ec?.toFixed(0)}</span>}
                                  </div>
                                : <span className="text-gray-300 text-xs italic">No data</span>}
                            </td>
                            <td className="px-4 py-3.5 text-right">
                              <button
                                onClick={() => setEditSoilModal({ device: d })}
                                className="text-xs px-2.5 py-1 border border-amber-200 text-amber-700 rounded-lg hover:bg-amber-50 whitespace-nowrap"
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {soilDevices.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-5 py-10 text-center text-gray-400 text-sm">
                            No soil sensors registered yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ══ GATEWAYS ═══════════════════════════════════════════════ */}
          {nav === 'gateways' && (
            <div className="space-y-5">
              <SectionHeader title="LoRaWAN Gateways" sub="Live metrics for all network gateways" />

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard label="Total Gateways"  value={gatewayList.length}                                                                       icon={<Radio     className="w-5 h-5 text-purple-600"/>} color="purple" />
                <KpiCard label="Online"           value={gatewayList.filter(g=>g.health.label==='Online').length}                                  icon={<Activity  className="w-5 h-5 text-green-600" />} color="green"  />
                <KpiCard label="Degraded/Offline" value={gatewayList.filter(g=>g.health.label!=='Online').length}                                  icon={<AlertTriangle className="w-5 h-5 text-yellow-600"/>} color="yellow" />
                <KpiCard label="Avg Devices/GW"   value={gatewayList.length ? Math.round(devices.length/gatewayList.length) : '—'}                icon={<TrendingUp className="w-5 h-5 text-blue-600" />} color="blue"   />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {gatewayList.map(gw => {
                  const rssiInfo = rssiLabel(gw.avgRssi);
                  return (
                    <div key={gw.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full shrink-0 mt-0.5 ${gw.health.dot}`} />
                          <div>
                            <h3 className="font-bold text-gray-900">{gw.shortName}</h3>
                            <p className="text-xs text-gray-400 font-mono truncate max-w-[200px]">{gw.id}</p>
                          </div>
                        </div>
                        <Badge color={gw.health.color==='green'?'green':gw.health.color==='yellow'?'yellow':'red'}>
                          {gw.health.label}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Devices</p>
                          <p className="font-bold text-gray-900 text-lg">{gw.devicesReached}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Avg RSSI</p>
                          <p className={`font-bold text-lg ${rssiInfo.color}`}>{gw.avgRssi ?? '—'}</p>
                          <p className={`text-[10px] ${rssiInfo.color}`}>{rssiInfo.text}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Last Seen</p>
                          <p className="font-bold text-gray-900 text-sm">{timeAgo(gw.lastSeen)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {gatewayList.length === 0 && <EmptyState icon={Radio} title="No gateways detected" sub="Gateways appear once devices report in" />}
              </div>
            </div>
          )}

          {/* ══ STATION NAMES ══════════════════════════════════════════ */}
          {nav === 'station-names' && (
            <div className="space-y-4">
              <SectionHeader
                title="Station Names & Locations"
                sub="Override default device IDs with human-readable names and location strings"
                actions={
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
                      className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none" />
                  </div>
                }
              />

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3 text-sm text-blue-800">
                <Info className="w-5 h-5 shrink-0 mt-0.5 text-blue-500" />
                <div>
                  <p className="font-semibold">How it works</p>
                  <p className="mt-0.5 text-blue-700">Names and locations saved here override the built-in defaults for all users. To add a brand-new device, deploy it via TTN console — it will appear here automatically once it sends data.</p>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden overflow-x-auto">
                <table className="w-full text-sm min-w-[600px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-5 py-3 font-semibold text-gray-600">Device ID</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Name</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden lg:table-cell">Location</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {devices.filter(d => !search || d.name?.toLowerCase().includes(q) || d.location?.toLowerCase().includes(q)).map(d => {
                      const saved = stationMeta.find(m => m.deviceId === d.id);
                      const isEditing = editStation?.deviceId === d.id;
                      return (
                        <tr key={d.id} className="hover:bg-gray-50">
                          <td className="px-5 py-3.5 text-gray-400 font-mono text-xs">{d.id?.slice(-10)}</td>
                          <td className="px-4 py-3.5">
                            {isEditing
                              ? <input value={editStation.name} onChange={e => setEditStation(s => ({ ...s, name: e.target.value }))}
                                  className="border border-purple-300 rounded-lg px-2 py-1 text-sm w-full focus:ring-2 focus:ring-purple-400 focus:outline-none" />
                              : <p className="font-medium text-gray-900">{d.name}</p>}
                          </td>
                          <td className="px-4 py-3.5 hidden lg:table-cell">
                            {isEditing
                              ? <input value={editStation.location} onChange={e => setEditStation(s => ({ ...s, location: e.target.value }))}
                                  placeholder="e.g. Butula, Busia County, Kenya"
                                  className="border border-purple-300 rounded-lg px-2 py-1 text-sm w-full focus:ring-2 focus:ring-purple-400 focus:outline-none" />
                              : <p className="text-gray-500 text-xs flex items-center gap-1">
                                  <MapPin className="w-3 h-3 shrink-0" />{d.location || 'No location set'}
                                </p>}
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-1 justify-end">
                              {isEditing ? (
                                <>
                                  <button onClick={() => handleSaveStation(editStation)}
                                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                                    <Save className="w-3.5 h-3.5" />Save
                                  </button>
                                  <button onClick={() => setEditStation(null)}
                                    className="text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50">
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => setEditStation({ deviceId: d.id, name: d.name || '', location: d.location || '' })}
                                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100">
                                    <Edit2 className="w-3.5 h-3.5" />Edit
                                  </button>
                                  {saved && (
                                    <button onClick={() => handleResetStation(d.id)}
                                      className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500">
                                      <RotateCcw className="w-3.5 h-3.5" />Reset
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ══ CUSTODIANS ═════════════════════════════════════════════ */}
          {nav === 'custodians' && (
            <div className="space-y-6">
              <SectionHeader title="Custodian Management" sub="Assign local station representatives" />

              {/* What is a custodian */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-500" />What is a Custodian?
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  A <strong>Custodian</strong> is a trusted person physically located at or near a weather station — typically a teacher, school administrator, or community leader. They are responsible for the day-to-day care of the station hardware.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { icon: <Eye className="w-5 h-5 text-blue-500" />, title: 'Monitor Data', desc: 'View live and historical readings for their assigned station(s).' },
                    { icon: <AlertTriangle className="w-5 h-5 text-orange-500" />, title: 'Report Issues', desc: 'Flag sensor malfunctions, physical damage, or obstructions.' },
                    { icon: <Activity className="w-5 h-5 text-green-500" />, title: 'Maintain Hardware', desc: 'Keep the device clean, unobstructed, and powered. Confirm solar panel and antenna are intact.' },
                  ].map(({ icon, title, desc }) => (
                    <div key={title} className="bg-gray-50 rounded-lg p-4 flex gap-3">
                      <div className="shrink-0 mt-0.5">{icon}</div>
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">{title}</p>
                        <p className="text-xs text-gray-500 mt-1">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Assign custodian */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-4">Assigned Custodians</h3>
                <div className="space-y-3">
                  {users.filter(u => u.role === 'custodian').length === 0
                    ? (
                      <div className="text-center py-8 text-gray-400">
                        <UserCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p className="font-medium text-gray-500">No custodians assigned yet</p>
                        <p className="text-sm mt-1">Go to <button onClick={() => setNav('users')} className="text-purple-600 underline">Users</button> and change a user's role to "Custodian", then use Access Control to assign their station.</p>
                      </div>
                    )
                    : users.filter(u => u.role === 'custodian').map(u => {
                      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
                      const assignedNames = (u.assignedDevices || []).map(id => {
                        const d = devices.find(x => x.id === id);
                        return d?.name || id;
                      });
                      return (
                        <div key={u.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold shrink-0">
                            {(u.firstName?.[0] || '?').toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900">{name}</p>
                            <p className="text-xs text-gray-400">{u.email}</p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {assignedNames.length === 0
                                ? <span className="text-xs text-gray-400 italic">No stations assigned — use Access Control</span>
                                : assignedNames.map(n => <Badge key={n} color="blue">{n}</Badge>)}
                            </div>
                          </div>
                          <button onClick={() => setAccessModal({ user: u, selectedDevices: u.assignedDevices || [], tier: u.accessTier || 'custom' })}
                            className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-100 shrink-0">
                            Edit Stations
                          </button>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {/* ══ API KEYS ═══════════════════════════════════════════════ */}
          {nav === 'api-keys' && (
            <div className="space-y-5">
              <SectionHeader
                title="API Keys"
                sub="Per-device API keys for commercial data access. Users call the proxy endpoint with their key — the master TTN key is never exposed."
              />

              {/* Endpoint reference card */}
              <div className="bg-gray-900 rounded-xl p-5 text-sm font-mono text-green-400 space-y-2">
                <p className="text-gray-400 text-xs font-sans mb-3 font-semibold uppercase tracking-wide">Proxy Endpoint (Base URL)</p>
                <p><span className="text-blue-400">GET</span> {window.location.origin.replace('3000','5000')}/api/v1/data/readings/<span className="text-yellow-400">{'{deviceId}'}</span></p>
                <p><span className="text-blue-400">GET</span> {window.location.origin.replace('3000','5000')}/api/v1/data/readings/latest</p>
                <p className="text-gray-400 text-xs font-sans mt-3">Auth: <span className="text-green-400">Authorization: ApiKey {'<key>'}</span> &nbsp;or&nbsp; <span className="text-green-400">?api_key={'<key>'}</span></p>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard label="Total Keys"   value={apiKeys.length}                                   icon={<Key      className="w-5 h-5 text-purple-600"/>} color="purple" />
                <KpiCard label="Active Keys"  value={apiKeys.filter(k=>k.isActive).length}             icon={<CheckCircle className="w-5 h-5 text-green-600"/>} color="green" />
                <KpiCard label="Revoked"      value={apiKeys.filter(k=>!k.isActive).length}            icon={<ZapOff   className="w-5 h-5 text-red-500"/>}    color="red" />
                <KpiCard label="Total Requests" value={apiKeys.reduce((s,k)=>s+(k.requestCount||0),0)} icon={<Activity className="w-5 h-5 text-blue-600"/>}   color="blue" />
              </div>

              {/* Keys table */}
              {apiKeyLoading
                ? <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 text-purple-400 animate-spin" /></div>
                : (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden overflow-x-auto">
                  <table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-5 py-3 font-semibold text-gray-600">User</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Key</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">Devices</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden lg:table-cell">Last Used</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden lg:table-cell">Requests</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {apiKeys.map(k => {
                        const userName = k.user ? `${k.user.firstName || ''} ${k.user.lastName || ''}`.trim() || k.user.email : '—';
                        return (
                          <tr key={k.id} className="hover:bg-gray-50">
                            <td className="px-5 py-3.5">
                              <p className="font-semibold text-gray-900 text-sm">{userName}</p>
                              <p className="text-gray-400 text-xs">{k.user?.email}</p>
                              {k.user?.accessTier && <Badge color="purple">{k.user.accessTier}</Badge>}
                            </td>
                            <td className="px-4 py-3.5">
                              <p className="font-mono text-xs text-gray-700 bg-gray-100 rounded px-2 py-1 inline-block">
                                {k.keyPrefix}••••••••••••
                              </p>
                              <p className="text-[10px] text-gray-400 mt-1">{k.name}</p>
                            </td>
                            <td className="px-4 py-3.5 hidden md:table-cell">
                              <p className="text-sm font-semibold text-gray-900">{k.deviceCount}</p>
                              {k.lastDeviceAccessed && <p className="text-[10px] text-gray-400 font-mono">{k.lastDeviceAccessed?.slice(-10)}</p>}
                            </td>
                            <td className="px-4 py-3.5 hidden lg:table-cell text-gray-400 text-xs">
                              {k.lastUsed ? timeAgo(k.lastUsed) : 'Never'}
                            </td>
                            <td className="px-4 py-3.5 hidden lg:table-cell text-gray-700 text-sm font-semibold">
                              {(k.requestCount || 0).toLocaleString()}
                            </td>
                            <td className="px-4 py-3.5">
                              <Badge color={k.isActive ? 'green' : 'red'}>
                                {k.isActive ? 'Active' : 'Revoked'}
                              </Badge>
                            </td>
                            <td className="px-4 py-3.5 text-right">
                              {k.isActive && (
                                <button onClick={() => handleRevokeKey(k.id)}
                                  className="text-xs px-2.5 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 flex items-center gap-1 ml-auto">
                                  <ZapOff className="w-3 h-3" />Revoke
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {apiKeys.length === 0 && (
                        <tr><td colSpan={7} className="text-center text-gray-400 py-10 text-sm">No API keys yet. Generate one from the Access Control section.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Generate key for user */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <p className="font-semibold text-gray-800 mb-1">Generate API Key for User</p>
                <p className="text-xs text-gray-500 mb-4">The key will be scoped to that user's currently assigned devices. The user must have at least one device assigned.</p>
                <GenerateKeyForm users={users} onGenerate={handleGenerateKey} />
              </div>
            </div>
          )}

          {/* ══ TIER PRICING CONFIG ════════════════════════════════════ */}
          {nav === 'tier-config' && (() => {
            const draft = tierDraft || tierConfig;
            const setDraft = (tier, field, value) =>
              setTierDraft(prev => ({
                ...(prev || tierConfig),
                [tier]: { ...(prev || tierConfig)[tier], [field]: value === '' ? null : (field === 'maxDevices' ? parseInt(value) || 0 : (field === 'price' ? parseFloat(value) || 0 : value)) },
              }));
            const save = () => {
              setTierConfig(draft);
              localStorage.setItem('anga_tier_config', JSON.stringify(draft));
              setTierDraft(null);
              showToast('Tier configuration saved');
            };
            return (
              <div className="space-y-5">
                <SectionHeader
                  title="Tier Pricing & Limits"
                  sub="Configure prices and station allowances for each subscription tier"
                  actions={
                    <div className="flex gap-2">
                      {tierDraft && (
                        <button onClick={() => setTierDraft(null)}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Discard</button>
                      )}
                      <button onClick={save}
                        className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">
                        <Save className="w-4 h-4" />Save Changes
                      </button>
                    </div>
                  }
                />

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {Object.entries(draft).map(([key, cfg]) => {
                    const colors = { basic:'gray', education:'blue', research:'purple', enterprise:'green', custom:'orange' };
                    const color = colors[key] || 'gray';
                    const bgCls = { gray:'bg-gray-50 border-gray-200', blue:'bg-blue-50 border-blue-200', purple:'bg-purple-50 border-purple-200', green:'bg-green-50 border-green-200', orange:'bg-orange-50 border-orange-200' };
                    return (
                      <div key={key} className={`rounded-xl border p-5 ${bgCls[color]}`}>
                        <div className="flex items-center gap-2 mb-4">
                          <Badge color={color}>{cfg.label}</Badge>
                          {key === 'basic' && <span className="text-xs text-gray-400">Free tier</span>}
                        </div>
                        <div className="space-y-3">
                          {/* Price */}
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                              Monthly Price ({cfg.currency || 'KES'})
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number" min="0" step="100"
                                value={cfg.price ?? ''}
                                placeholder="Custom / Contact"
                                onChange={e => setDraft(key, 'price', e.target.value)}
                                className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none bg-white"
                              />
                              <span className="text-xs text-gray-400">KES</span>
                            </div>
                            {cfg.price == null && (
                              <p className="text-xs text-gray-400 mt-1">Empty = "Custom pricing" label</p>
                            )}
                          </div>
                          {/* Max devices */}
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">
                              Max Stations Allowed
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number" min="1" max="999"
                                value={cfg.maxDevices >= 999 ? '' : cfg.maxDevices}
                                placeholder="Unlimited"
                                onChange={e => setDraft(key, 'maxDevices', e.target.value === '' ? 999 : e.target.value)}
                                className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none bg-white"
                              />
                              <span className="text-xs text-gray-400">stations</span>
                            </div>
                            {cfg.maxDevices >= 999 && (
                              <p className="text-xs text-gray-400 mt-1">Empty = Unlimited access</p>
                            )}
                          </div>
                          {/* Preview */}
                          <div className="pt-2 border-t border-black/10">
                            <p className="text-xs text-gray-500">
                              Displayed as: <strong>
                                {cfg.price == null ? 'Custom' : cfg.price === 0 ? 'Free' : `KES ${cfg.price.toLocaleString()}/mo`}
                              </strong>
                              {' · '}
                              {cfg.maxDevices >= 999 ? 'All stations' : `Up to ${cfg.maxDevices} stations`}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800 flex gap-3">
                  <Info className="w-5 h-5 shrink-0 mt-0.5 text-blue-500" />
                  <div>
                    <p className="font-semibold">About tier pricing</p>
                    <p className="mt-0.5 text-blue-700">These prices are shown to admins during access assignment. Payment processing is handled externally — this config is for reference and display only. Settings are saved locally and persist across sessions.</p>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ══ LIVE MAP ════════════════════════════════════════════════ */}
          {nav === 'map' && (
            <div className="space-y-4">
              {selectedStation ? (
                <WeatherStationDashboard
                  stationId={selectedStation}
                  stationInfo={selectedStationInfo}
                  onBack={() => { setSelectedStation(null); setSelectedStationInfo(null); }}
                  userData={userData}
                />
              ) : (
                <>
                  <SectionHeader title="Live Network Map" sub="Click a marker to view its dashboard" />
                  <div className="rounded-xl overflow-hidden border border-gray-200" style={{ height: 'calc(100vh - 200px)' }}>
                    <DevicesMap onStationSelect={handleStationSelect} />
                  </div>
                </>
              )}
            </div>
          )}

          {/* ══ SETTINGS ═══════════════════════════════════════════════ */}
          {nav === 'settings' && (
            <div className="space-y-5">
              <SectionHeader title="Settings" sub="Platform configuration" />
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <p className="text-gray-500 text-sm">Platform settings will be available here in a future update.</p>
                <div className="mt-4 p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
                  <p className="font-semibold">SMTP Email</p>
                  <p className="mt-1">Configure in <code className="bg-blue-100 px-1 rounded">api-master/.env</code> — EMAIL_USER, EMAIL_PASS (Gmail App Password)</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Messages ── */}
          {nav === 'messages' && (
            <div style={{ margin: -24, height: 'calc(100vh - 0px)', display: 'flex', flexDirection: 'column' }}>
              <MessagingPanel isAdmin onUnreadChange={setMsgUnread} />
            </div>
          )}

          {/* ══ SITE TRAFFIC ═══════════════════════════════════════════ */}
          {nav === 'traffic' && (() => {
            const VIEW_LABELS = {
              map: 'Map', dashboard: 'Dashboard', analytics: 'Analytics',
              nearest: 'Nearest', subscription: 'Subscription', api: 'API Keys',
              messages: 'Messages', admin: 'Admin', profile: 'Profile',
            };
            const fmt = (v) => VIEW_LABELS[v] || v;
            const BLUE = '#1D72B8';

            if (trafficLoading) return (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            );
            if (trafficError) return (
              <div className="flex flex-col items-center justify-center h-64 gap-3">
                <AlertTriangle className="w-8 h-8 text-red-400" />
                <p className="text-sm font-medium text-red-500">{trafficError}</p>
                <p className="text-xs text-gray-400">Make sure the backend is running and restarted after the last deploy.</p>
                <button
                  onClick={() => { setTrafficLoading(true); setTrafficError(null); fetchTrafficStats().then(setTrafficStats).catch(e => setTrafficError(e.message)).finally(() => setTrafficLoading(false)); }}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium border border-blue-200 rounded-lg px-4 py-2"
                >
                  <RefreshCw className="w-4 h-4" /> Retry
                </button>
              </div>
            );
            if (!trafficStats) return (
              <div className="flex items-center justify-center h-64 text-gray-400 text-sm">No traffic data yet — navigate around the app to generate events.</div>
            );

            const { summary, byView, daily, recent } = trafficStats;
            const dailyForChart = daily.map(d => ({
              date: new Date(d.date).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' }),
              Views: d.views,
              Users: d.uniqueUsers,
            }));
            const byViewForChart = byView.map(d => ({ name: fmt(d.view), Views: d.count }));

            return (
              <div className="space-y-6">
                <SectionHeader title="Site Traffic" sub="Page-view analytics — last 30 days" />

                {/* Summary cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { label: 'Views Today',         value: summary.todayViews,       icon: Activity,  color: 'text-blue-600',   bg: 'bg-blue-50'   },
                    { label: 'Unique Users Today',  value: summary.todayUniqueUsers, icon: Users,     color: 'text-purple-600', bg: 'bg-purple-50' },
                    { label: 'Total Views (All)',   value: summary.totalAllTime,     icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50'  },
                  ].map(({ label, value, icon: Icon, color, bg }) => (
                    <div key={label} className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
                      <div className={`w-11 h-11 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
                        <Icon className={`w-5 h-5 ${color}`} />
                      </div>
                      <div>
                        <p className="text-2xl font-black text-gray-900">{value?.toLocaleString() ?? '—'}</p>
                        <p className="text-xs text-gray-500 font-medium">{label}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Charts row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {/* Views by section */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <p className="text-sm font-bold text-gray-800 mb-4">Views by Section <span className="text-gray-400 font-normal">(last 7 days)</span></p>
                    {byViewForChart.length === 0
                      ? <p className="text-gray-400 text-sm text-center py-8">No data yet</p>
                      : (
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={byViewForChart} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                            <Bar dataKey="Views" fill={BLUE} radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )
                    }
                  </div>

                  {/* Daily trend */}
                  <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <p className="text-sm font-bold text-gray-800 mb-4">Daily Trend <span className="text-gray-400 font-normal">(last 30 days)</span></p>
                    {dailyForChart.length === 0
                      ? <p className="text-gray-400 text-sm text-center py-8">No data yet</p>
                      : (
                        <ResponsiveContainer width="100%" height={220}>
                          <LineChart data={dailyForChart} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                            <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                            <Line type="monotone" dataKey="Views" stroke={BLUE} strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="Users" stroke="#a855f7" strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      )
                    }
                  </div>
                </div>

                {/* Recent activity feed */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100">
                    <p className="text-sm font-bold text-gray-800">Recent Page Views</p>
                  </div>
                  <div className="divide-y divide-gray-50 max-h-80 overflow-y-auto">
                    {recent.length === 0
                      ? <p className="text-gray-400 text-sm text-center py-8">No views recorded yet</p>
                      : recent.map((r, i) => (
                          <div key={i} className="flex items-center gap-3 px-5 py-2.5">
                            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                              <span className="text-blue-700 text-[10px] font-bold">
                                {(r.userEmail?.[0] || '?').toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate">{r.userEmail || 'Unknown'}</p>
                              <p className="text-[10px] text-gray-400">{r.userRole || '—'}</p>
                            </div>
                            <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full shrink-0">
                              {fmt(r.view)}
                            </span>
                            <span className="text-[10px] text-gray-400 shrink-0">
                              {new Date(r.at).toLocaleString('en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        ))
                    }
                  </div>
                </div>

                {/* Refresh */}
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setTrafficLoading(true);
                      fetchTrafficStats().then(setTrafficStats).catch(() => {}).finally(() => setTrafficLoading(false));
                    }}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <RefreshCw className="w-4 h-4" /> Refresh
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ══ SYSTEM LOGS ════════════════════════════════════════════ */}
          {nav === 'logs' && <LogsPanel />}

          {/* ── Profile / Account Settings ── */}
          {nav === 'profile' && (
            <div style={{ margin: -24 }}>
              <ProfilePage
                userData={userData}
                darkMode={darkMode}
                setDarkMode={setDarkMode}
                onUserUpdate={(updated) => { /* userData is prop — notify parent if needed */ }}
              />
            </div>
          )}

        </div>
      </main>

      {/* ── USER ACTION DROPDOWN (fixed position, avoids overflow clipping) ── */}
      {openMenuId && menuPos && (() => {
        const u = users.find(x => x.id === openMenuId);
        if (!u) return null;
        const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username;
        return (
          <>
            {/* Transparent overlay — closes menu on outside click */}
            <div className="fixed inset-0 z-30" onClick={() => setOpenMenuId(null)} />
            <div className="fixed z-40 w-44 bg-white border border-gray-200 rounded-xl shadow-xl py-1 text-sm"
              style={{ top: menuPos.top, right: menuPos.right }}>
              {!u.emailVerified && (
                <>
                  <button onClick={() => { setOpenMenuId(null); handleResendOtp(u.id, u.email); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-indigo-700 hover:bg-indigo-50">
                    <Send className="w-4 h-4" />Resend OTP
                  </button>
                  <button onClick={() => { setOpenMenuId(null); handleForceVerify(u.id, u.email, u.accountType); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-amber-700 hover:bg-amber-50"
                    title="Manually verify email without OTP">
                    <UserCheck className="w-4 h-4" />Force Verify Email
                  </button>
                </>
              )}
              {u.isActive
                ? <button onClick={() => { setOpenMenuId(null); setSuspendModal({ userId: u.id, name }); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-yellow-700 hover:bg-yellow-50">
                    <Ban className="w-4 h-4" />Suspend
                  </button>
                : <button onClick={() => { setOpenMenuId(null); handleActivate(u.id); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-green-700 hover:bg-green-50">
                    <Unlock className="w-4 h-4" />Reactivate
                  </button>
              }
              <button onClick={() => { setOpenMenuId(null); setAccessModal({ user: u, selectedDevices: u.assignedDevices || [], tier: u.accessTier || 'basic' }); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-blue-700 hover:bg-blue-50">
                <Lock className="w-4 h-4" />Manage Access
              </button>
              <div className="border-t border-gray-100 my-1" />
              <button onClick={() => { setOpenMenuId(null); setDeleteModal({ userId: u.id, name }); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />Delete
              </button>
            </div>
          </>
        );
      })()}

      {/* ── MODALS ────────────────────────────────────────────────────── */}

      {/* Reject */}
      {rejectModal && (
        <Modal title={`Reject: ${rejectModal.name}`} onClose={() => { setRejectModal(null); setRejectReason(''); }}>
          <p className="text-sm text-gray-600 mb-3">This reason will be emailed to the user.</p>
          <textarea rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            placeholder="e.g. Student ID image is unclear. Please resubmit with a clearer photo."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-red-400 focus:outline-none" />
          <div className="flex gap-3 justify-end mt-4">
            <button onClick={() => { setRejectModal(null); setRejectReason(''); }}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleRejectSubmit}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">Send Rejection</button>
          </div>
        </Modal>
      )}

      {/* Suspend */}
      {suspendModal && (
        <Modal title={`Suspend: ${suspendModal.name}`} onClose={() => { setSuspendModal(null); setSuspendReason(''); setSuspendDuration('indefinite'); }}>
          <p className="text-sm text-gray-600 mb-4">The user will be locked out and notified. You can reactivate at any time.</p>
          <div className="mb-4">
            <p className="text-sm font-semibold text-gray-700 mb-2">Suspension period</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: '1d',        label: '1 Day'      },
                { value: '3d',        label: '3 Days'     },
                { value: '7d',        label: '1 Week'     },
                { value: '30d',       label: '30 Days'    },
                { value: '90d',       label: '90 Days'    },
                { value: 'indefinite',label: 'Indefinite' },
              ].map(opt => (
                <button key={opt.value}
                  onClick={() => setSuspendDuration(opt.value)}
                  className={`py-2 rounded-lg text-sm font-medium border transition-all ${
                    suspendDuration === opt.value
                      ? 'border-yellow-500 bg-yellow-50 text-yellow-800'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <textarea rows={2} value={suspendReason} onChange={e => setSuspendReason(e.target.value)}
            placeholder="Optional: reason for suspension (internal note)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-yellow-400 focus:outline-none" />
          <div className="flex gap-3 justify-end mt-4">
            <button onClick={() => { setSuspendModal(null); setSuspendReason(''); setSuspendDuration('indefinite'); }}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleSuspend}
              className="px-4 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium">Suspend Account</button>
          </div>
        </Modal>
      )}

      {/* Delete */}
      {deleteModal && (
        <Modal title="Delete User" onClose={() => setDeleteModal(null)}>
          <p className="text-sm text-gray-600 mb-4">
            Are you sure you want to permanently delete <strong>{deleteModal.name}</strong>? This cannot be undone.
          </p>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setDeleteModal(null)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handleDelete}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">Delete Permanently</button>
          </div>
        </Modal>
      )}

      {/* Purge Suspended */}
      {purgeSuspendedModal && (
        <Modal title="Purge All Suspended Accounts" onClose={() => setPurgeSuspendedModal(false)}>
          <div className="flex items-start gap-3 mb-4">
            <AlertOctagon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-800 font-semibold mb-1">This action is irreversible.</p>
              <p className="text-sm text-gray-600">
                All suspended (inactive) accounts will be <strong>permanently deleted</strong> from the database.
                This cannot be undone. Active accounts and your own account will not be affected.
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setPurgeSuspendedModal(false)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={handlePurgeSuspended}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center gap-2">
              <Trash2 className="w-4 h-4" />Delete All Suspended
            </button>
          </div>
        </Modal>
      )}

      {/* ── Edit Soil Sensor Modal ─────────────────────────────────────────── */}
      {editSoilModal && (
        <EditSoilModal
          device={editSoilModal.device}
          devices={devices}
          users={users}
          showToast={showToast}
          onClose={() => setEditSoilModal(null)}
          onSave={(deviceId, patch, assignedUsers) =>
            setSoilDevices(prev => prev.map(s =>
              s.deviceId === deviceId ? { ...s, ...patch, assignedUsers } : s
            ))
          }
        />
      )}

      {/* Assign Custodian */}
      {assignCustodianModal && (() => {
        const { device, currentCustodian } = assignCustodianModal;
        const eligibleUsers = users.filter(u => u.isActive && u.emailVerified && u.id !== currentCustodian?.id);
        return (
          <Modal title={`Assign Custodian — ${device.name}`} onClose={() => setAssignCustodianModal(null)}>
            <p className="text-sm text-gray-600 mb-4">
              Select a user to become the custodian of this station. They will receive <strong>free data access</strong> as a reward for maintaining the equipment.
            </p>
            {currentCustodian && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                <p className="font-semibold text-green-800">Current custodian</p>
                <p className="text-green-700">{currentCustodian.firstName} {currentCustodian.lastName} — {currentCustodian.email}</p>
              </div>
            )}
            <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100 mb-4">
              {eligibleUsers.map(u => {
                const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username;
                return (
                  <button key={u.id} onClick={() => handleAssignCustodian(device, u.id)}
                    className="w-full text-left px-4 py-3 hover:bg-purple-50 flex items-center justify-between gap-3 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{name}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </div>
                    <Badge color={u.role === 'custodian' ? 'green' : 'gray'}>
                      {getRoleDisplayName(u.role)}
                    </Badge>
                  </button>
                );
              })}
              {eligibleUsers.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">No eligible users found</p>
              )}
            </div>
            <div className="flex justify-end">
              <button onClick={() => setAssignCustodianModal(null)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            </div>
          </Modal>
        );
      })()}

      {/* Document viewer */}
      {docModal && (
        <Modal title={`Document — ${docModal.name}`} wide onClose={() => setDocModal(null)}>
          <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ height: '60vh' }}>
            <iframe
              src={`${getDocumentUrl(docModal.userId)}?token=${localStorage.getItem('authToken') || ''}`}
              className="w-full h-full border-0"
              title="Verification document"
            />
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <a href={getDocumentUrl(docModal.userId)} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Download className="w-4 h-4" />Open in new tab
            </a>
            <button onClick={() => setDocModal(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Close</button>
          </div>
        </Modal>
      )}

      {/* Access Control modal */}
      {accessModal && (
        <Modal title={`Access: ${accessModal.user.firstName} ${accessModal.user.lastName}`} wide onClose={() => setAccessModal(null)}>
          <div className="space-y-4">
            {/* Custodian banner — replaces tier selector */}
            {accessModal.user.role === 'custodian' ? (
              <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                <Shield className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-green-800">Station Custodian — Free Access</p>
                  <p className="text-xs text-green-700 mt-0.5">
                    Custodians receive free data access to their assigned station(s) as a reward for maintaining the equipment.
                    Select the station(s) this custodian is responsible for.
                  </p>
                </div>
              </div>
            ) : (
              /* Tier selection */
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Access Tier</p>
                <div className="grid grid-cols-2 gap-2">
                  {ACCESS_TIERS.filter(t => t.value !== 'custodian').map(t => (
                    <button key={t.value} onClick={() => setAccessModal(m => ({ ...m, tier: t.value }))}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        accessModal.tier === t.value
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'}`}>
                      <p className="font-semibold text-sm text-gray-900">{t.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Station selection — all tiers except Basic */}
            {(accessModal.user.role === 'custodian' || accessModal.tier !== 'basic') && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-700">
                    Select Stations
                    <span className="ml-2 font-normal text-gray-400">({accessModal.selectedDevices.length} of {devices.length} selected)</span>
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => setAccessModal(m => ({ ...m, selectedDevices: devices.map(d => d.id) }))}
                      className="text-xs text-purple-600 hover:underline">All</button>
                    <span className="text-gray-300">|</span>
                    <button onClick={() => setAccessModal(m => ({ ...m, selectedDevices: [] }))}
                      className="text-xs text-gray-500 hover:underline">None</button>
                  </div>
                </div>
                {accessModal.tier === 'custom' && accessModal.selectedDevices.length >= 5 && (
                  <div className="mb-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-800 font-medium">
                    Custom tier pricing: <strong>KES {(tierConfig.custom?.price ?? 1000).toLocaleString()}/mo</strong> for {accessModal.selectedDevices.length} stations
                  </div>
                )}
                <div className="border border-gray-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-gray-100">
                  {devices.map(d => {
                    const checked = accessModal.selectedDevices.includes(d.id);
                    return (
                      <label key={d.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox" checked={checked}
                          onChange={() => setAccessModal(m => ({
                            ...m,
                            selectedDevices: checked
                              ? m.selectedDevices.filter(x => x !== d.id)
                              : [...m.selectedDevices, d.id],
                          }))}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-400" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{d.name}</p>
                          <p className="text-xs text-gray-400 truncate">{d.location || d.id}</p>
                        </div>
                        <Badge color={d.status==='online'?'green':'red'}>{d.status}</Badge>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setAccessModal(null)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSaveAccess} className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">
                Save Access
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* New API Key reveal — shown once */}
      {newKeySecret && (
        <Modal title="API Key Generated" onClose={() => setNewKeySecret(null)}>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800 font-medium">Copy this key now — it will never be shown again.</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1 font-semibold">{newKeySecret.name}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-900 text-green-400 text-xs rounded-lg p-3 font-mono break-all select-all">
                  {newKeySecret.key}
                </code>
                <button onClick={() => navigator.clipboard.writeText(newKeySecret.key).then(() => showToast('Copied!'))}
                  className="p-2 text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 shrink-0">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Scoped to {newKeySecret.deviceIds?.length} device(s)</p>
              <p className="text-xs text-gray-500">Usage: <code className="bg-gray-100 px-1 rounded">Authorization: ApiKey {newKeySecret.key?.slice(0,18)}…</code></p>
            </div>
            <div className="flex justify-end">
              <button onClick={() => setNewKeySecret(null)} className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">Done</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Add User Modal ──────────────────────────────────────────────── */}
      {addUserModal && (
        <AddUserModal
          onClose={() => setAddUserModal(false)}
          onCreated={() => { setAddUserModal(false); loadUsers(1); showToast('User created successfully'); }}
          showToast={showToast}
          devices={devices}
        />
      )}
    </div>
  );
};

// ── Add User Modal ────────────────────────────────────────────────────────────
const AddUserModal = ({ onClose, onCreated, showToast, devices = [] }) => {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '',
    role: 'user', accessTier: 'basic', phone: '',
    assignedDevices: [],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isCustodian = form.role === 'custodian';

  const toggleDevice = (id) => {
    setForm(f => ({
      ...f,
      assignedDevices: f.assignedDevices.includes(id)
        ? f.assignedDevices.filter(x => x !== id)
        : [...f.assignedDevices, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email) {
      setErr('First name, last name and email are required.');
      return;
    }
    if (isCustodian && form.assignedDevices.length === 0) {
      setErr('Please select at least one station for this custodian.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await adminCreateUser({
        firstName:  form.firstName.trim(),
        lastName:   form.lastName.trim(),
        email:      form.email.trim().toLowerCase(),
        role:       form.role,
        accessTier: isCustodian ? 'custodian' : form.accessTier,
        assignedDevices: isCustodian ? form.assignedDevices : [],
        ...(form.phone && { phone: form.phone.trim() }),
      });
      onCreated();
    } catch (ex) {
      setErr(ex.message || 'Failed to create user');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><User className="w-5 h-5 text-purple-600" />Add New User</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100"><CloseX className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">First Name *</label>
              <input value={form.firstName} onChange={e => set('firstName', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none"
                placeholder="Jane" required />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Last Name *</label>
              <input value={form.lastName} onChange={e => set('lastName', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none"
                placeholder="Doe" required />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Email Address *</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none"
              placeholder="jane@example.com" required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Phone (optional)</label>
            <input value={form.phone} onChange={e => set('phone', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none"
              placeholder="+254 7XX XXX XXX" />
          </div>
          <div className={isCustodian ? '' : 'grid grid-cols-2 gap-3'}>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Role *</label>
              <select value={form.role} onChange={e => set('role', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none">
                {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {!isCustodian && (
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Access Tier *</label>
                <select value={form.accessTier} onChange={e => set('accessTier', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none">
                  {TIER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Custodian: station picker */}
          {isCustodian && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">
                Assigned Stations * <span className="text-gray-400 font-normal">({form.assignedDevices.length} selected)</span>
              </label>
              <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto p-2 space-y-1">
                {devices.length === 0
                  ? <p className="text-xs text-gray-400 p-2">No stations loaded</p>
                  : devices.map(d => (
                    <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={form.assignedDevices.includes(d.id)}
                        onChange={() => toggleDevice(d.id)}
                        className="accent-purple-600"
                      />
                      <span className="flex-1 truncate">{d.name}</span>
                      <span className={`text-xs font-semibold ${d.status === 'online' ? 'text-green-600' : 'text-gray-400'}`}>
                        {d.status}
                      </span>
                    </label>
                  ))
                }
              </div>
            </div>
          )}

          <p className="text-xs text-gray-400">
            The account will be created immediately active. The user will receive an email to set their own password.
          </p>
          {err && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{err}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={busy}
              className="px-5 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2">
              {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create User
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Generate Key inline form ──────────────────────────────────────────────────
const GenerateKeyForm = ({ users, onGenerate }) => {
  const [selectedUser, setSelectedUser] = React.useState('');
  const [busy,         setBusy]         = React.useState(false);
  const eligible = users.filter(u => u.isActive && (u.assignedDevices || []).length > 0);

  const handle = async () => {
    if (!selectedUser) return;
    setBusy(true);
    const u = eligible.find(x => x.id === selectedUser);
    await onGenerate(selectedUser, `${u?.firstName || ''} ${u?.lastName || ''}`.trim());
    setBusy(false);
    setSelectedUser('');
  };

  return (
    <div className="flex gap-3 items-end">
      <div className="flex-1">
        <label className="block text-xs font-semibold text-gray-600 mb-1">Select User</label>
        <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 focus:outline-none">
          <option value="">— choose a user —</option>
          {eligible.map(u => (
            <option key={u.id} value={u.id}>
              {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email} ({u.assignedDevices?.length} devices)
            </option>
          ))}
        </select>
        {eligible.length === 0 && <p className="text-xs text-yellow-600 mt-1">No users with assigned devices found. Assign devices first.</p>}
      </div>
      <button disabled={!selectedUser || busy} onClick={handle}
        className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-40 flex items-center gap-2 shrink-0">
        {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        Generate Key
      </button>
    </div>
  );
};

// ── Modal wrapper ─────────────────────────────────────────────────────────────
const Modal = ({ title, children, onClose, wide = false }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className={`bg-white rounded-2xl shadow-2xl w-full flex flex-col ${wide ? 'max-w-2xl' : 'max-w-md'}`} style={{ maxHeight: '90vh' }}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
        <h3 className="font-bold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100">
          <CloseX className="w-5 h-5" />
        </button>
      </div>
      <div className="overflow-y-auto p-6">{children}</div>
    </div>
  </div>
);

export default SuperAdminDashboard;
